// "Thank personally" (Marketing Hub row 2.6; plan section 4.8, decision 27;
// build spec screen 2 and modal 28): every review and every referral gets
// two thank-yous, the advisor's within a day and the owner's card in his
// own hand within the week. This file holds the rules and the words; the
// rows are read and written in shared/db/marketing-thank-you.ts.
//
// CONSUMER: shared (the Today route, the Today page, tests).
//
// WHAT THE OS DOES, AND NEVER DOES. It lists who is owed a thank-you, shows
// the address and a suggested wording, and records what a person did ("I
// mailed the card", "I called", "skip, because..."). It never sends,
// prints, orders or mails anything itself (Grant, 2026-09-26: no vendor,
// nothing automated). The "Send the email" button waits for a later PR.
//
// WHICH ITEMS GET ROWS (Grant, 2026-10-02): only the ones that arrive from
// now on. Production then held 1,045 referrals, every one written by the
// backfill or by one correction batch on 2026-09-29, and 3,872 reviews, all
// from ActiveCampaign or the Gravity Forms import (the newest July 10). The
// spec's own rule ("closed or introduced in the last 7 days") would have
// put 923 years-old closed referrals on the list. So:
//   * the referral was introduced, or the review submitted, on or after
//     THANK_YOU_SINCE; and
//   * it was not written by the backfill or the ActiveCampaign migration
//     (NOT_THANKABLE_SOURCES), so a later re-run of either adds no rows.
// DECISION: the item's own date, not the day the OS stored it. A CSV
// import of old reviews stores them today with their old dates, and those
// are history being filed, not a family to thank this week. The cost: an
// introduction an advisor types in weeks late, dated before the cutoff,
// gets no row; nothing before 2026-10-02 can.
//
// ROW 4.3 (2026-10-09) COMPLETED THE TWO THANK-YOUS. The advisor's can now
// go as an email from their OWN Outlook (never anyone else's), once Grant
// has approved the wording; as a text, which is a task with the words
// (decision 22: the OS never sends a text); or as a card or a call, as
// before. The per-company legal line is DATA (communication_templates,
// slug thank-you-rule-<company>); THANK_YOU_RULE_FALLBACKS below holds the
// same words only until a row exists.
//
// Hard rule: PURE. No database, no React.

import { renderTemplate } from "../communications/template-merge";
import { companyWord } from "./today";

/** Items dated before this day never get a thank-you row. */
export const THANK_YOU_SINCE = "2026-10-02T00:00:00.000Z";

/** Sources whose rows are history, never a new thank-you. */
export const NOT_THANKABLE_SOURCES = {
  referral: ["backfill"],
  review: ["migration"],
} as const;

export const THANK_YOU_SUBJECTS = ["referral", "review"] as const;
export type ThankYouSubject = (typeof THANK_YOU_SUBJECTS)[number];

/** owner: the card in Grant's own hand. advisor: the advisor's own. */
export const THANK_YOU_SIDES = ["owner", "advisor"] as const;
export type ThankYouSide = (typeof THANK_YOU_SIDES)[number];

/** The referral table's own four words; 'note' is the card in a person's hand. */
export const THANK_YOU_CHANNELS = ["note", "email", "text", "call"] as const;
export type ThankYouChannel = (typeof THANK_YOU_CHANNELS)[number];

/** What a person records from the page: the card they mailed, the call they
 *  made, the text they sent from their own phone, or a skip with a reason.
 *  None of these sends anything; an email goes through its own route. */
export const THANK_YOU_ACTS = ["mailed", "called", "texted", "skipped"] as const;
export type ThankYouAct = (typeof THANK_YOU_ACTS)[number];

export function isThankYouSubject(s: unknown): s is ThankYouSubject {
  return typeof s === "string" && (THANK_YOU_SUBJECTS as readonly string[]).includes(s);
}
export function isThankYouSide(s: unknown): s is ThankYouSide {
  return typeof s === "string" && (THANK_YOU_SIDES as readonly string[]).includes(s);
}
export function isThankYouAct(s: unknown): s is ThankYouAct {
  return typeof s === "string" && (THANK_YOU_ACTS as readonly string[]).includes(s);
}

/** The channel each act records. A skip records none. */
export function channelForAct(act: ThankYouAct): ThankYouChannel | null {
  if (act === "mailed") return "note";
  if (act === "called") return "call";
  if (act === "texted") return "text";
  return null;
}

/** The touch each act leaves on the sender's household (null: none). */
export function touchForAct(act: ThankYouAct): "card_sent" | "meeting_phone" | "text_sent" | null {
  if (act === "mailed") return "card_sent";
  if (act === "called") return "meeting_phone";
  if (act === "texted") return "text_sent";
  return null;
}

/** The advisor's thank-you is owed within a day of the item (plan 4.8). */
export const ADVISOR_THANK_YOU_HOURS = 24;

/** When the advisor's thank-you falls due: a day after the introduction or
 *  the review. Grant's card has the week and no clock on the row. */
export function advisorThankYouDueAt(dated: string): string {
  return new Date(new Date(dated).getTime() + ADVISOR_THANK_YOU_HOURS * 3_600_000).toISOString();
}

/** A skip carries a reason a person can read later; this long at most. */
export const SKIP_REASON_MAX = 200;

/** The reason trimmed to the cap, or null when there is none. */
export function cleanSkipReason(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const t = raw.trim().slice(0, SKIP_REASON_MAX);
  return t === "" ? null : t;
}

/**
 * An item is thankable when it arrived on or after the cutoff and is not
 * history from the backfill or the migration. The SQL readers apply the
 * same two tests; this is the rule the tests pin.
 */
export function isThankable(subject: ThankYouSubject, item: { source: string; dated: string }): boolean {
  if ((NOT_THANKABLE_SOURCES[subject] as readonly string[]).includes(item.source)) return false;
  return item.dated >= THANK_YOU_SINCE;
}

/**
 * Whether a side is still owed: nobody has thanked and nobody has skipped.
 * One stamp of either closes it.
 */
export function isOwed(side: { thanked_at: string | null; skipped_at: string | null }): boolean {
  return side.thanked_at === null && side.skipped_at === null;
}

// ─── Words ────────────────────────────────────────────────────────────

/** Plan 4.8's legal table, one row per client company: the line printed on
 *  every thank-you draft and referral record, and the why behind it. The
 *  rows live in communication_templates (slug thank-you-rule-<company>,
 *  migration 20261009005231); these are the fallback until a row exists,
 *  word for word the same as the seed. */
export interface ThankYouRule {
  slug: string;
  line: string;
  why: string;
}

export function thankYouRuleSlug(companySlug: string): string {
  return `thank-you-rule-${companySlug}`;
}

const RESPA_WHY =
  "RESPA Section 8, per the CFPB's FAQ: a gift or incentive given for referral of settlement-service business as part of an agreement or understanding violates the Act; there is no minimum value; a pattern of gifts establishes the understanding.";

export const THANK_YOU_RULE_FALLBACKS: Record<string, ThankYouRule> = {
  mortgage: {
    slug: thankYouRuleSlug("mortgage"),
    line: "Mortgage: a note. Never a gift, discount, drawing or reward tied to the referral (RESPA).",
    why: RESPA_WHY,
  },
  realty: {
    slug: thankYouRuleSlug("realty"),
    line: "Realty: a note. Never a gift, discount, drawing or reward tied to the referral (RESPA).",
    why: RESPA_WHY,
  },
  insurance: {
    slug: thankYouRuleSlug("insurance"),
    line: "Insurance: a note. Any payment to an unlicensed person needs counsel's line.",
    why: "A.R.S. 20-282 and 20-298: payment only to persons who do not sell, solicit or negotiate, and never where anti-rebating applies.",
  },
  financial: {
    slug: thankYouRuleSlug("financial"),
    line: "Financial: a note. Any compensation for a testimonial or endorsement triggers the SEC marketing rule's disclosure and, above $1,000 in 12 months, a written agreement.",
    why: "SEC Rule 206(4)-1 and the 2025 examinations risk alert.",
  },
  planning: {
    slug: thankYouRuleSlug("planning"),
    line: "Planning: a note. Medicare referrals follow the CMS rules on beneficiary contact and TPMO data sharing.",
    why: "Section 5.7's Medicare pack: the CMS rules on beneficiary contact and third-party marketing organization data sharing.",
  },
};

/** The rules as read from the database: company slug to its line and why,
 *  with whether a template row (not the fallback) supplied it. */
export type ThankYouRules = Record<string, { line: string; why: string; from_template: boolean }>;

/** The per-company line every thank-you prints (section 4.8: a note, never
 *  a gift or reward, on every company). Reads the data rows when given;
 *  the fallback carries the same words until a row exists. */
export function companyRule(slug: string | null, rules: ThankYouRules | null = null): string {
  if (slug === null) return "A note. Never a gift or reward.";
  const stored = rules?.[slug]?.line;
  if (stored && stored.trim() !== "") return stored;
  return THANK_YOU_RULE_FALLBACKS[slug]?.line ?? `${companyWord(slug)}: a note. Never a gift or reward.`;
}

/** The template rows Grant may approve for sending from the OS: the
 *  thank-yous (row 4.3), the review ask and its follow-up (row 4.4), the
 *  introduction ask (row 4.5). */
export const APPROVABLE_WORDING_SLUGS = [
  // Program audit M18: the line a Financial event's public page prints.
  "event-financial-line",
  "nbh-thank-you-referral",
  "nbh-thank-you-review",
  // Row 4.4: the review ask and its one follow-up take the same stamp.
  "nbh-email-file-completed",
  "nbh-email-review-follow-up",
  // Row 4.5: the introduction ask.
  "nbh-email-introduction-due",
  // Row 6.7: the household digest's email and its six section wordings.
  "digest-email",
  "digest-section-market",
  "digest-section-home",
  "digest-section-renewal",
  "digest-section-window",
  "digest-section-question",
  "digest-section-privacy",
  // Row 6.8: the yearly check-in the digest carries.
  "digest-section-checkin",
  // Slice 6 audit L8: the subject test's second subject line.
  "digest-email-subject-b",
] as const;

/** The refusal when the wording has not been approved for sending (row 4.0:
 *  the thank-you's words are held for Grant's approval before anything can
 *  send them). A card, a call or a text never needs it: the person wrote
 *  those words themselves. */
export const WORDING_UNAPPROVED_MESSAGE =
  "This wording has not been approved for sending yet. Write the card, call, or text instead; the email opens once Grant approves the words.";

/** "Text" on a thank-you is a task with the words (decision 22). The task's
 *  row lives in communication_templates (thank-you-text-task); this is the
 *  fallback until the row exists. Fields: {{name}}, {{first_name}},
 *  {{reason}}, {{company_name}}, {{sender_first_name}}, {{due}}. */
export const THANK_YOU_TEXT_TASK_FALLBACK = {
  slug: "thank-you-text-task",
  subject: "Text {{name}}: thank them for {{reason}}",
  body:
    'Hi {{first_name}}, this is {{sender_first_name}} at {{company_name}}. Thank you for {{reason}}. It means a great deal to us, and I wanted you to hear it from me.\n\nSend it from your own phone, then tap "I sent the text" on the thank-you. Due {{due}}.',
};

/** "sending the Okafors our way" / "your review of Stewardship Realty". */
export function thankYouReason(subject: ThankYouSubject, input: { family: string | null; companySlug: string | null }): string {
  if (subject === "referral") return `sending ${input.family ?? "a family"} our way`;
  return `your review of ${input.companySlug ? `Stewardship ${companyWord(input.companySlug)}` : "Stewardship"}`;
}

/** The task's title and words for one thank-you. */
export function thankYouTextTask(
  stored: { subject_template: string; body_template: string } | null,
  merge: { name: string; first_name: string; reason: string; company_name: string; sender_first_name: string; due: string },
): { title: string; description: string } {
  return {
    title: renderTemplate(stored?.subject_template ?? THANK_YOU_TEXT_TASK_FALLBACK.subject, merge),
    description: renderTemplate(stored?.body_template ?? THANK_YOU_TEXT_TASK_FALLBACK.body, merge),
  };
}

/** The suggested wording's fallbacks, used only until the template row is
 *  missing (migration 20261002193446 seeds both). */
export const THANK_YOU_FALLBACKS: Record<ThankYouSubject, { slug: string; subject: string; body: string }> = {
  referral: {
    slug: "nbh-thank-you-referral",
    subject: "Thank you for sending {{family}} our way",
    body:
      "{{first_name}},\n\nYou sent {{family}} to Stewardship {{company}}, and I wanted you to hear a thank-you from me personally.\n\nThank you for trusting us with people you care about. That is the whole business.\n\n{{sender_first_name}}",
  },
  review: {
    slug: "nbh-thank-you-review",
    subject: "Thank you for your review",
    body:
      "{{first_name}},\n\nThank you for taking the time to write about Stewardship {{company}}. A review is how a family who does not know us yet decides to call, so yours will help someone.\n\nWe are grateful you trust us.\n\n{{sender_first_name}}",
  },
};

/** "Maria", from "Maria Reyes"; "there" when nobody is named. */
function firstWord(name: string | null): string {
  const t = (name ?? "").trim();
  return t === "" ? "there" : t.split(/\s+/)[0]!;
}

/**
 * The wording filled in for one row. `template` is the stored row's body
 * when there is one. The sender is whoever writes the thank-you: Grant for
 * his card, the advisor for theirs.
 */
export function thankYouWording(
  subject: ThankYouSubject,
  input: {
    template: string | null;
    greetName: string | null;
    family: string | null;
    companySlug: string | null;
    senderName: string | null;
  },
): string {
  return renderTemplate(input.template ?? THANK_YOU_FALLBACKS[subject].body, {
    first_name: firstWord(input.greetName),
    family: input.family ?? "a family",
    company: input.companySlug ? companyWord(input.companySlug) : "",
    sender_first_name: firstWord(input.senderName),
  }).replace(/Stewardship\s+([.,])/g, "Stewardship$1");
}

/** "2214 E. Ivy Ln, Gilbert, AZ 85298", or null when no street is on file
 *  (a card needs a street; a city alone is not an address). */
export function addressLine(a: {
  street: string | null;
  street2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
} | null): string | null {
  if (!a) return null;
  const street = [a.street, a.street2].map((s) => (s ?? "").trim()).filter((s) => s !== "").join(" ");
  if (street === "") return null;
  const cityState = [a.city, a.state].map((s) => (s ?? "").trim()).filter((s) => s !== "").join(", ");
  const tail = [cityState, (a.zip ?? "").trim()].filter((s) => s !== "").join(" ");
  return tail === "" ? street : `${street}, ${tail}`;
}

// ─── What the route answers ───────────────────────────────────────────

export interface ThankYouSideState {
  thanked_at: string | null;
  channel: ThankYouChannel | null;
  skipped_at: string | null;
  skipped_reason: string | null;
}

export interface ThankYouRow {
  subject: ThankYouSubject;
  /** The referral's or the review's id. */
  id: string;
  /** Whose thank-you this row is. */
  side: ThankYouSide;
  company_slug: string | null;
  /** The introduction day, or the day the review was submitted. */
  dated: string;
  /** When the advisor's thank-you falls due (a day after `dated`). */
  advisor_due_at: string;
  /** Who is being thanked. family: a person in the book (a household).
   *  partner: a vendor. unlinked: the referral names nobody. */
  thanked: { kind: "family" | "partner" | "unlinked"; household_id: string | null; name: string | null };
  /** The one person named on the referral or the review (gate 16: a
   *  two-adult household thanks the person named, not the household). */
  thanked_contact_id: string | null;
  /** Whether Grant has approved this wording for sending as an email. */
  wording_approved: boolean;
  /** A referral's referred family. Null for a review. */
  referred: { household_id: string; name: string } | null;
  /** The review's stars and words, or a referral's status word. */
  rating_stars: number | null;
  quote: string | null;
  status: string | null;
  /** INTERNAL_ONLY: the referral's own note, shown when nobody is linked. */
  note: string | null;
  advisor: { user_id: string; name: string } | null;
  advisor_side: ThankYouSideState;
  owner_side: ThankYouSideState;
  /** The thanked household's mailing address, for the card. */
  address: string | null;
  wording: string;
  wording_subject: string;
  company_rule: string;
}

/** What the thank-you email composer opens with (GET), one type for the
 *  server and the page. The same shape as Today's composer, without the
 *  one question. */
export interface ThankYouEmailComposer {
  outlook_connected: boolean;
  blocked: { reason: "impersonating" | "role" | "unapproved" | "no_household"; message: string } | null;
  recipients: Array<{ contact_id: string; name: string; email: string; is_primary: boolean; is_named: boolean; notes?: string[] }>;
  not_emailable: Array<{
    contact_id: string;
    name: string;
    /** "refused": the one press-time rule's other reasons (program audit
     *  PR 1); `sentence` then says which, in plain words. */
    reason: "opted_out" | "suppressed" | "no_email" | "refused";
    sentence?: string;
  }>;
  default_contact_ids: string[];
  subject: string;
  body: string;
  template_slug: string;
  from_template: boolean;
  has_signature: boolean;
  /** The per-company line, printed on the draft and never sent. */
  rule_line: string;
}

export interface ThankYouList {
  items: ThankYouRow[];
  /** COUNT(*) over the same WHERE, never the page's length. */
  total: number;
  /** The newest thank-you this side recorded, for the empty state. */
  last_thanked_at: string | null;
}

/** "The Okafors left a five-star review · Realty", "The Villarreals sent
 *  the Okafors · Realty". */
export function thankYouTitle(row: Pick<ThankYouRow, "subject" | "thanked" | "referred" | "rating_stars" | "company_slug">): string {
  const who = row.thanked.name ?? (row.thanked.kind === "unlinked" ? "Someone not linked in the OS" : "A family");
  const company = row.company_slug ? ` · ${companyWord(row.company_slug)}` : "";
  if (row.subject === "review") {
    const stars = row.rating_stars && row.rating_stars >= 1 && row.rating_stars <= 5 ? `a ${STAR_WORD[row.rating_stars]}-star review` : "a review";
    return `${who} left ${stars}${company}`;
  }
  return `${who} sent ${row.referred?.name ?? "a family"}${company}`;
}

const STAR_WORD: Record<number, string> = { 1: "one", 2: "two", 3: "three", 4: "four", 5: "five" };

/** What the thank-you is for, in a few words: "sending Chidi Okafor our
 *  way", "a five-star review: \"Great team\"". */
export function thankYouFor(row: Pick<ThankYouRow, "subject" | "referred" | "rating_stars" | "quote">): string {
  if (row.subject === "referral") return `sending ${row.referred?.name ?? "a family"} our way`;
  const stars = row.rating_stars && row.rating_stars >= 1 && row.rating_stars <= 5 ? `a ${STAR_WORD[row.rating_stars]}-star review` : "a review";
  return row.quote ? `${stars}: "${row.quote}"` : stars;
}

/** The suffix the "who sent you" form answer puts on a name the client typed
 *  (shared/marketing/referral-record.ts submittedSourceFrom). */
const AS_THEY_WROTE_IT = " (as they wrote it)";

/**
 * The row's words for a referral whose sender is not linked to a person.
 * The note is the opportunity's source text copied onto the referral
 * (shared/db/referral-from-opportunity.ts), so the line says where it came
 * from instead of presenting a bare name next to "not linked" (Brian Ream,
 * 2026-10-29: "Tim Mace... I have no idea where it got that?").
 * DECISION: origin is read from the note's own wording, not a new column.
 * Only the client's form answer leaves a trace; a name typed by a teammate
 * and a name set on a form look the same, so the line says "recorded", not
 * who typed it (the opportunity's history has that).
 * HUB-OK: a wording fix and one button on the already-shipped row 2.6; the build program's register is unchanged.
 */
export function unlinkedSenderLine(note: string | null): string {
  const t = (note ?? "").trim();
  if (t === "") return "who sent them is not linked to a person in the OS yet";
  if (t.endsWith(AS_THEY_WROTE_IT)) {
    return `the client wrote this on the form: "${t.slice(0, -AS_THEY_WROTE_IT.length)}". It is not linked to a person in the OS yet`;
  }
  return `the Source on their opportunity says "${t}", recorded when it was opened. It is not linked to a person in the OS yet`;
}

/** "due today", "due Oct 4", "owed since Oct 3": the advisor's clock in words. */
export function advisorDueLine(dueAt: string, nowIso: string, dayWord: (iso: string) => string): string {
  const due = new Date(dueAt).getTime();
  const now = new Date(nowIso).getTime();
  if (now > due) return `owed since ${dayWord(dueAt)}`;
  if (due - now <= 24 * 3_600_000) return "due today";
  return `due ${dayWord(dueAt)}`;
}

/** "Nate thanked them Oct 3 · a card", "Nate has not thanked them yet". */
export function advisorSideLine(row: Pick<ThankYouRow, "advisor" | "advisor_side">, dayWord: (iso: string) => string): string {
  const first = row.advisor ? firstWord(row.advisor.name) : null;
  const s = row.advisor_side;
  if (s.thanked_at) {
    return `${first ?? "The advisor"} thanked them ${dayWord(s.thanked_at)}${s.channel ? ` · ${CHANNEL_WORD[s.channel]}` : ""}`;
  }
  if (s.skipped_at) return `${first ?? "The advisor"} skipped theirs${s.skipped_reason ? `: ${s.skipped_reason}` : ""}`;
  return first ? `${first} has not thanked them yet` : "No advisor on file for this one";
}

export const CHANNEL_WORD: Record<ThankYouChannel, string> = {
  note: "a card",
  email: "an email",
  text: "a text",
  call: "a call",
};
